import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  setIsNewForBasicInformation,
  setIsDataChangedForBasic,
  addErrorMessage,
  addSuccessMessage,
  setIsLoading,
  setBasicInformationData,
} from "../slice";
import { BASIC_INFORMATION_API } from "../api";
import { useDate } from "./";

export const useBasicInfo = ({ hasEditAccess, errors }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { persian, persian_fa, gregorian, gregorian_en, convertDate, simpleGregorianToPersian } =
    useDate();
  const { data, isNew, isDataChanged } = useSelector((state) => state.BasicInformation);

  const { mutateAsync: addBasicInformation } = useMutation((body) =>
    BASIC_INFORMATION_API.AddBasicInformation(body)
  );

  const { mutateAsync: editBasicInformation } = useMutation((body) =>
    BASIC_INFORMATION_API.EditBasicInformation(body)
  );

  const { mutate: getBasicInformation } = useMutation(
    () => BASIC_INFORMATION_API.GetBasicInformation(),
    {
      onSuccess: ({ data: basicInformation }) => {
        dispatch(setIsLoading(false));
        if (basicInformation.success && basicInformation.data) {
          let birthDate = "";
          if (basicInformation.data.birthDate) {
            birthDate = simpleGregorianToPersian(basicInformation.data.birthDate.split("T")[0]);
          }
          dispatch(
            setBasicInformationData({
              ...basicInformation.data,
              birthDate,
            })
          );
        }
      },
      staleTime: Infinity,
    }
  );

  const countErrors = (errors) => {
    let result = 0;
    Object.keys(errors).forEach((item) => {
      if (errors[item].length !== 0) result += 1;
    });
    return result;
  };

  const handleSubmit = async (route) => {
    if (!isDataChanged) {
      navigate(route);
      return;
    }
    let body = { ...data };
    if (body.birthDate) {
      body.birthDate = convertDate(
        body.birthDate.split("T")[0],
        { calendar: persian, locale: persian_fa, format: "YYYY-MM-DD" },
        { calendar: gregorian, locale: gregorian_en, format: "YYYY-MM-DDTHH:mm:ss.SSS Z" }
      );
    }
    if (countErrors(errors) === 0) {
      if (hasEditAccess) {
        if (isNew) {
          await addBasicInformation(body, {
            onSuccess: ({ data }) => {
              if (data.success) {
                dispatch(setIsNewForBasicInformation(false));
                dispatch(addSuccessMessage(data?.message));
                navigate(route);
              }
            },
            onSettled: () => {
              dispatch(setIsDataChangedForBasic(false));
            },
            onError: ({ response }) => {
              dispatch(addErrorMessage(response));
            },
          });
        } else {
          await editBasicInformation(body, {
            onSuccess: ({ data }) => {
              if (data.success) {
                dispatch(setIsNewForBasicInformation(false));
                dispatch(addSuccessMessage(data?.message));
                navigate(route);
              }
            },
            onSettled: () => {
              dispatch(setIsDataChangedForBasic(false));
            },
            onError: ({ response }) => {
              dispatch(addErrorMessage(response));
            },
          });
        }
      } else {
        navigate(isNew);
      }
    }
  };
  return { handleSubmit, getBasicInformation };
};
